import React, { useState, useEffect, MouseEvent } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Library,
  X,
  Trash2,
  Loader2,
  User,
  Sparkles,
} from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * Saved character data structure from API
 */
export interface SavedCharacter {
  id: string;
  user_id: string;
  name: string;
  description?: string;
  portrait_url: string;
  art_style: string;
  color_palette: string;
  times_used: number;
  last_used_at?: string;
  created_at: string;
}

/**
 * Props for SavedCharactersLibrary component
 */
interface SavedCharactersLibraryProps {
  isOpen: boolean;
  onClose: () => void;
  onSelectCharacter: (character: SavedCharacter) => void;
}

/**
 * Helper to get full image URL
 */
const getImageUrl = (url: string | undefined | null): string | undefined => {
  if (!url) return undefined;
  if (url.startsWith("/static/")) {
    return `${BACKEND_URL}${url}`;
  }
  return url;
};

/**
 * SavedCharactersLibrary Component
 *
 * Modal showing the user's saved character library.
 * Allows selecting a character for use in a new story or deleting characters.
 */
const SavedCharactersLibrary: React.FC<SavedCharactersLibraryProps> = ({
  isOpen,
  onClose,
  onSelectCharacter,
}) => {
  const [characters, setCharacters] = useState<SavedCharacter[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);
  const [deleting, setDeleting] = useState<boolean>(false);

  // Fetch saved characters when modal opens
  useEffect(() => {
    if (isOpen) {
      fetchCharacters();
    }
  }, [isOpen]);

  const fetchCharacters = async (): Promise<void> => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/v1/user-characters`, {
        withCredentials: true,
      });
      setCharacters(response.data.characters || []);
    } catch (error) {
      console.error("Failed to fetch saved characters:", error);
      toast.error("Failed to load your characters");
    } finally {
      setLoading(false);
    }
  };

  const handleSelect = async (character: SavedCharacter): Promise<void> => {
    // Record usage before selecting
    try {
      await axios.post(
        `${API}/v1/user-characters/${character.id}/use`,
        {},
        { withCredentials: true }
      );
    } catch (error) {
      // Non-critical, just log
      console.error("Failed to record character usage:", error);
    }

    onSelectCharacter(character);
    onClose();
  };

  const handleDeleteClick = (
    e: MouseEvent<HTMLButtonElement>,
    characterId: string
  ): void => {
    e.stopPropagation();
    setDeleteConfirmId(characterId);
  };

  const handleDeleteConfirm = async (): Promise<void> => {
    if (!deleteConfirmId) return;

    setDeleting(true);
    try {
      await axios.delete(`${API}/v1/user-characters/${deleteConfirmId}`, {
        withCredentials: true,
      });

      // Remove from local state
      setCharacters((prev) => prev.filter((c) => c.id !== deleteConfirmId));
      toast.success("Character removed from library");
    } catch (error) {
      console.error("Failed to delete character:", error);
      toast.error("Failed to delete character");
    } finally {
      setDeleting(false);
      setDeleteConfirmId(null);
    }
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Modal Backdrop */}
      <div
        className="fixed inset-0 z-50 flex items-center justify-center p-4"
        style={{
          backdropFilter: "blur(8px)",
          backgroundColor: "rgba(0, 0, 0, 0.4)",
        }}
        onClick={onClose}
      >
        {/* Modal Content */}
        <div
          className="relative w-full max-w-2xl max-h-[80vh] rounded-2xl overflow-hidden flex flex-col"
          style={{
            background: "var(--st-bg-surface)",
            border: "1px solid var(--st-border-light)",
          }}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div
            className="flex items-center justify-between px-6 py-4 border-b"
            style={{ borderColor: "var(--st-border-light)" }}
          >
            <div className="flex items-center gap-3">
              <div
                className="w-10 h-10 rounded-xl flex items-center justify-center"
                style={{ background: "#F3E8FF" }}
              >
                <Library className="w-5 h-5" style={{ color: "#9B59B6" }} />
              </div>
              <div>
                <h2
                  className="text-lg font-bold"
                  style={{
                    fontFamily: "Quicksand, sans-serif",
                    color: "var(--st-text-heading)",
                  }}
                >
                  My Characters
                </h2>
                <p
                  className="text-xs"
                  style={{ color: "var(--st-text-muted)" }}
                >
                  Select a character to add to your story
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
            >
              <X className="w-5 h-5" style={{ color: "var(--st-text-muted)" }} />
            </button>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto p-6">
            {loading ? (
              <div className="flex flex-col items-center justify-center py-12">
                <Loader2
                  className="w-8 h-8 animate-spin mb-3"
                  style={{ color: "var(--st-primary)" }}
                />
                <p style={{ color: "var(--st-text-muted)" }}>
                  Loading your characters...
                </p>
              </div>
            ) : characters.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12">
                <div
                  className="w-16 h-16 rounded-full flex items-center justify-center mb-4"
                  style={{ background: "var(--st-bg-muted)" }}
                >
                  <User
                    className="w-8 h-8"
                    style={{ color: "var(--st-text-muted)" }}
                  />
                </div>
                <h3
                  className="text-lg font-semibold mb-2"
                  style={{
                    fontFamily: "Quicksand, sans-serif",
                    color: "var(--st-text-heading)",
                  }}
                >
                  No saved characters yet
                </h3>
                <p
                  className="text-sm text-center max-w-sm"
                  style={{ color: "var(--st-text-muted)" }}
                >
                  After transforming a photo into a character, you can save it
                  here to reuse in future stories without needing to transform
                  again.
                </p>
              </div>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                {characters.map((character) => (
                  <div
                    key={character.id}
                    onClick={() => handleSelect(character)}
                    className="group relative p-3 rounded-xl cursor-pointer transition-all hover:shadow-lg hover:scale-[1.02]"
                    style={{
                      background: "var(--st-bg-elevated)",
                      border: "1px solid var(--st-border-light)",
                    }}
                  >
                    {/* Portrait */}
                    <div className="relative aspect-square rounded-lg overflow-hidden mb-3">
                      <img
                        src={getImageUrl(character.portrait_url)}
                        alt={character.name}
                        className="w-full h-full object-cover"
                      />

                      {/* Select overlay on hover */}
                      <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-all flex items-center justify-center opacity-0 group-hover:opacity-100">
                        <Sparkles className="w-6 h-6 text-white" />
                      </div>

                      {/* Delete button */}
                      <button
                        onClick={(e) => handleDeleteClick(e, character.id)}
                        className="absolute top-2 right-2 p-1.5 rounded-lg opacity-0 group-hover:opacity-100 transition-all hover:scale-110"
                        style={{
                          background: "rgba(0, 0, 0, 0.6)",
                        }}
                        title="Remove from library"
                      >
                        <Trash2 className="w-3.5 h-3.5 text-white" />
                      </button>
                    </div>

                    {/* Info */}
                    <h4
                      className="font-semibold text-sm truncate"
                      style={{
                        fontFamily: "Quicksand, sans-serif",
                        color: "var(--st-text-heading)",
                      }}
                    >
                      {character.name}
                    </h4>

                    {character.description && (
                      <p
                        className="text-xs line-clamp-1 mt-0.5"
                        style={{ color: "var(--st-text-muted)" }}
                      >
                        {character.description}
                      </p>
                    )}

                    {/* Usage badge */}
                    {character.times_used > 0 && (
                      <div
                        className="mt-2 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs"
                        style={{
                          background: "var(--st-bg-muted)",
                          color: "var(--st-text-muted)",
                        }}
                      >
                        Used {character.times_used}{" "}
                        {character.times_used === 1 ? "time" : "times"}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Delete Confirmation Dialog */}
      <AlertDialog
        open={!!deleteConfirmId}
        onOpenChange={(open: boolean) => !open && setDeleteConfirmId(null)}
      >
        <AlertDialogContent className="rounded-2xl border-0 p-0 overflow-hidden max-w-md">
          <div className="p-6">
            <AlertDialogHeader className="flex flex-col items-center text-center space-y-4">
              <div
                className="w-14 h-14 rounded-full flex items-center justify-center"
                style={{ background: "#FEE2E2" }}
              >
                <Trash2 className="w-7 h-7" style={{ color: "#EF4444" }} />
              </div>

              <AlertDialogTitle
                className="text-xl font-bold"
                style={{
                  fontFamily: "Quicksand, sans-serif",
                  color: "var(--st-text-heading)",
                }}
              >
                Remove Character?
              </AlertDialogTitle>

              <AlertDialogDescription
                className="text-sm leading-relaxed"
                style={{
                  fontFamily: "Quicksand, sans-serif",
                  color: "var(--st-text-muted)",
                }}
              >
                This character will be removed from your library. You can always
                save it again by transforming a new photo.
              </AlertDialogDescription>
            </AlertDialogHeader>

            <AlertDialogFooter className="flex-row gap-3 mt-6 sm:space-x-0">
              <AlertDialogCancel
                className="flex-1 h-11 rounded-xl font-semibold border-0 dark:hover:bg-gray-600"
                style={{
                  background: "var(--st-bg-surface)",
                  color: "var(--st-text-heading)",
                }}
                disabled={deleting}
              >
                Cancel
              </AlertDialogCancel>

              <AlertDialogAction
                onClick={(e: React.MouseEvent) => {
                  e.preventDefault();
                  handleDeleteConfirm();
                }}
                className="flex-1 h-11 rounded-xl font-semibold text-white border-0 hover:opacity-90 transition-opacity"
                style={{
                  background: "linear-gradient(135deg, #EF4444 0%, #DC2626 100%)",
                }}
                disabled={deleting}
              >
                {deleting ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  "Remove"
                )}
              </AlertDialogAction>
            </AlertDialogFooter>
          </div>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

export default SavedCharactersLibrary;
